import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Tabs, Tab, Spinner, InputGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { loginWithPasswordApi, requestOtpApi, loginWithOtpApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { FaGoogle, FaFacebookF, FaApple, FaEnvelope } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { triggerMediumHaptic } from '../utils/haptics';
import './LoginPage.css';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();

    // Form States
    const [loginMethod, setLoginMethod] = useState('password');
    const [identifier, setIdentifier] = useState(''); // Email or Mobile
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // UI States
    const [otpSent, setOtpSent] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' }); // Combined error/info state
    const [loading, setLoading] = useState(false);

    // Reset messages when switching tabs
    const handleTabSelect = (k) => {
        setLoginMethod(k);
        setOtpSent(false);
        setMessage({ type: '', text: '' });
    };

    const handleSuccessfulLogin = (apiResponseData) => {
         console.log("Full Login Response:", apiResponseData);
    console.log("Role Code:", apiResponseData.roleCode);
    console.log("Role Code Type:", typeof apiResponseData.roleCode)
        login(apiResponseData.token, {
            id: apiResponseData.id,
            fullName: apiResponseData.fullName,
            email: apiResponseData.email,
            role: apiResponseData.role,
            roleCode: apiResponseData.roleCode,
            joget_user_id: apiResponseData.joget_user_id,
              parentUserId: apiResponseData.parentUserId || null,
        permissions: apiResponseData.permissions || [] 
        });
    //   const rCode = String(apiResponseData.roleCode || apiResponseData.role_code);

    // // 400 = Casting Director, 900 = Staff
    // if (rCode === '400' || rCode === '900') {
    //     navigate('/dashboard/overview');
    // } else if (rCode === '300') {
    //     navigate('/dashboard');
    // } else {
    //     navigate('/dashboard');
    // }
         navigate('/dashboard');
    
        // navigate('/dashboard');
    };

    const handleError = (err) => {
        const errorMsg = err.response?.data?.message || 'Something went wrong. Please try again.';
        setMessage({ type: 'danger', text: errorMsg });
    };

    const handlePasswordLogin = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setLoading(true);
        try {
            const { data } = await loginWithPasswordApi({ email: identifier, password });
            handleSuccessfulLogin(data);
        } catch (err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setLoading(true);
        try {
            const { data } = await requestOtpApi({ identifier });
            setOtpSent(true);
            setMessage({ type: 'success', text: data.message || 'OTP sent successfully!' });
        } catch (err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await loginWithOtpApi({ identifier, otp });
            handleSuccessfulLogin(data);
        } catch (err) {
            setMessage({ type: 'danger', text: 'Invalid or expired OTP.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            pageTitle="Login"
            sidebarTitle="Welcome Back"
            subtitle="Sign in to your account to continue"
             className="login-auth-layout" 
        >
            <Tabs
                activeKey={loginMethod}
                onSelect={handleTabSelect}
                className="mb-4 custom-login-tabs"
                justify
            >
                {/* --- Password Login Tab --- */}
                <Tab eventKey="password" title="Password">
                    {message.text && message.type === 'danger' && (
                        <Alert variant="danger" className="py-2 small">{message.text}</Alert>
                    )}
                    <Form onSubmit={handlePasswordLogin}>
                        <Form.Group className="mb-4">
                            <Form.Label className="form-label-custom d-none">Email or Mobile</Form.Label>
                            <InputGroup className="login-input-group">
                                <InputGroup.Text><Mail size={18}/></InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter your email or mobile number"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    required
                                    className="form-ctrl-login-custom"
                                />
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="form-label-custom d-none">Password</Form.Label>
                            <InputGroup className="login-input-group">
                                <InputGroup.Text><Lock size={18}/></InputGroup.Text>
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="form-ctrl-login-custom"
                                />
                                <InputGroup.Text 
                                    className="cursor-pointer" 
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                                </InputGroup.Text>
                            </InputGroup>
                        </Form.Group>

                        <div className="remember-forgot-wrapper d-flex justify-content-between align-items-center mb-4">
                            <Form.Check
                                type="checkbox"
                                label={<span className="text-secondary small">Remember me</span>}
                                className="custom-checkbox"
                            />
                            <Link to="/forgot-password" size="sm" className="forgot-link small">
                                Forgot password?
                            </Link>
                        </div>

                        <div className="sticky-mobile-cta">
                            {/* <Button type="submit" className="btn-auth-primary w-100 d-flex justify-content-center align-items-center gap-2 native-btn" disabled={loading} onClick={() => triggerMediumHaptic()}>
                                {loading ? <Spinner size="sm" animation="border" /> : (
                                    <>SIGN IN <ArrowRight size={18} /></>
                                )}
                            </Button> */}
                            <Button 
                                type="submit" 
                                className="btn-auth-primary w-100 native-btn" 
                                disabled={loading} 
                                onClick={() => triggerMediumHaptic()}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" animation="border" />
                                        <span>SIGNING IN...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>SIGN IN</span>
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Tab>

                {/* --- OTP Login Tab --- */}
                <Tab eventKey="otp" title="Login with OTP">
                    {message.text && (
                        <Alert variant={message.type === 'danger' ? 'danger' : 'info'} className="py-2 small">
                            {message.text}
                        </Alert>
                    )}

                    {!otpSent ? (
                        <Form onSubmit={handleRequestOtp}>
                            <Form.Group className="mb-4">
                                <InputGroup className="login-input-group">
                                    <InputGroup.Text><Mail size={18}/></InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter registered Email or Mobile"
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        required
                                        className="form-ctrl-login-custom"
                                    />
                                </InputGroup>
                            </Form.Group>
                            <div className="sticky-mobile-cta">
                                {/* <Button type="submit" className="btn-auth-primary w-100 d-flex justify-content-center align-items-center gap-2 native-btn" disabled={loading} onClick={() => triggerMediumHaptic()}>
                                    {loading ? <Spinner size="sm" animation="border" /> : (
                                        <>SEND OTP <ArrowRight size={18} /></>
                                    )}
                                </Button> */}
                                <Button 
                                    type="submit" 
                                    className="btn-auth-primary w-100 native-btn" 
                                    disabled={loading} 
                                    onClick={() => triggerMediumHaptic()}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" animation="border" />
                                            <span>SENDING OTP...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>SEND OTP</span>
                                            <ArrowRight size={18} />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    ) : (
                        <Form onSubmit={handleOtpLogin}>
                            <Form.Group className="mb-4">
                                <Form.Label className="text-secondary text-center d-block small mb-3">
                                    Enter 6-digit code sent to <br /> <strong className="text-white">{identifier}</strong>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="000000"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="form-ctrl-login-custom text-center otp-input"
                                    style={{ letterSpacing: '0.4em', fontSize: '24px', fontWeight: 'bold' }}
                                    required
                                    autoFocus
                                />
                            </Form.Group>
                            <div className="sticky-mobile-cta">
                                {/* <Button type="submit" className="btn-auth-primary w-100 d-flex justify-content-center align-items-center gap-2 native-btn" disabled={loading || otp.length < 4} onClick={() => triggerMediumHaptic()}>
                                    {loading ? <Spinner size="sm" animation="border" /> : (
                                        <>VERIFY & LOGIN <ArrowRight size={18} /></>
                                    )}
                                </Button> */}
                                <Button 
                                    type="submit" 
                                    className="btn-auth-primary w-100 native-btn" 
                                    disabled={loading || otp.length < 4} 
                                    onClick={() => triggerMediumHaptic()}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" animation="border" />
                                            <span>VERIFYING...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>VERIFY & LOGIN</span>
                                            <ArrowRight size={18} />
                                        </>
                                    )}
                                </Button>
                            </div>
                            <div className="text-center mt-3">
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    onClick={() => setOtpSent(false)} 
                                    className="text-secondary small text-decoration-none"
                                >
                                    Change Email/Mobile
                                </Button>
                            </div>
                        </Form>
                    )}
                </Tab>
            </Tabs>

            <div className="social-divider">
                <span>or continue with</span>
            </div>
            
            <div className="social-login-row">
                <button type="button" className="social-btn native-btn"><FcGoogle size={20} /></button>
                <button type="button" className="social-btn native-btn"><FaFacebookF size={18} color="#1877f2" /></button>
                <button type="button" className="social-btn native-btn"><FaApple size={20} color="#fff" /></button>
                <button type="button" className="social-btn native-btn"><FaEnvelope size={18} color="#a0a0a0" /></button>
            </div>

            <div className="text-center mt-4">
                <p className="small text-secondary">
                    Don't have an account? <Link to="/register" className="signup-link">Sign up</Link>
                </p>
            </div>
        </AuthLayout>
    );
};

export default LoginPage;